import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IPasswordValidationFields {
	allowedCharactersOk: boolean;
	allowedCharactersMessage: string;
	lengthOk: boolean;
	passwordLengthMessage: string;
	digitsOk: boolean;
	digitsMessage: string;
	uppercaseOk: boolean;
	uppercaseMessage: string;
	lowercaseOk: boolean;
	lowercaseMessage: string;
	specialCharOk: boolean;
	specialCharMessage: string;
	passwordConfirmMessage: string;
	passwordConfirmOK: boolean;
	spaceCharExists: boolean;
	spaceCharExistsMessage: string;
}

export class PasswordValidationFields implements IPasswordValidationFields {
	public allowedCharactersOk: boolean;
	public allowedCharactersMessage: string;
	public lengthOk: boolean;
	public passwordLengthMessage: string;
	public digitsOk: boolean;
	public digitsMessage: string;
	public uppercaseOk: boolean;
	public uppercaseMessage: string;
	public lowercaseOk: boolean;
	public lowercaseMessage: string;
	public specialCharOk: boolean;
	public specialCharMessage: string;
	public passwordConfirmMessage: string;
	public passwordConfirmOK: boolean;
	public spaceCharExists: boolean;
	public spaceCharExistsMessage: string;

	constructor(fields?: IPasswordValidationFields) {
		let newFields: IPasswordValidationFields = fields || {
			allowedCharactersOk: null,
			allowedCharactersMessage:
				'Το Password θα πρέπει να είναι από 8 ως 15 λατινικοί χαρακτήρες, αριθμοί ή σύμβολα',
			digitsMessage: 'Πρέπει να περιέχεται τουλάχιστον ένας αριθμός',
			digitsOk: null,
			lengthOk: null,
			lowercaseMessage: 'Περιέχεται τουλάχιστον ένας πεζός λατινικός χαρακτήρας',
			lowercaseOk: null,
			passwordLengthMessage:
				'Το Password θα πρέπει να είναι από 8 ως 15 λατινικοί χαρακτήρες, αριθμοί ή σύμβολα',
			specialCharMessage:
				'Πρέπει να περιέχεται τουλάχιστον ένας ειδικός χαρακτήρας (`~!@#$%^&*()-_=+[{]}|;:\'"<,>./?€£)',
			specialCharOk: null,
			uppercaseMessage: 'Περιέχεται τουλάχιστον ένας κεφαλαίος λατινικός χαρακτήρας',
			uppercaseOk: null,
			passwordConfirmMessage: null,
			passwordConfirmOK: null,
			spaceCharExists: null,
			spaceCharExistsMessage: 'Δεν επιτρέπεται ο κενός χαρακτήρας',
		};
		this.read(newFields);
	}

	private read(newFields: IPasswordValidationFields) {
		this.digitsMessage = newFields.digitsMessage;
		this.digitsOk = newFields.digitsOk;
		this.lengthOk = newFields.lengthOk;
		this.lowercaseMessage = newFields.lowercaseMessage;
		this.lowercaseOk = newFields.lowercaseOk;
		this.passwordLengthMessage = newFields.passwordLengthMessage;
		this.specialCharMessage = newFields.specialCharMessage;
		this.specialCharOk = newFields.specialCharOk;
		this.uppercaseMessage = newFields.uppercaseMessage;
		this.uppercaseOk = newFields.uppercaseOk;
		this.passwordConfirmMessage = newFields.passwordConfirmMessage;
		this.passwordConfirmOK = newFields.passwordConfirmOK;
		this.spaceCharExists = newFields.spaceCharExists;
		this.spaceCharExistsMessage = newFields.spaceCharExistsMessage;
	}
}

export class PasswordValidator {
	public static validation: IPasswordValidationFields = new PasswordValidationFields();
	private static _isValidPassSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private static _isValidRepeatPassSub: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public static readonly isValidPass$ = PasswordValidator._isValidPassSub.asObservable();
	public static readonly isValidRepeatPass$ =
		PasswordValidator._isValidRepeatPassSub.asObservable();

	public static get isValidPassAndConfirm$(): Observable<boolean> {
		return combineLatest(
			PasswordValidator.isValidPass$,
			PasswordValidator.isValidRepeatPass$
		).pipe(map((val) => val[0] && val[1]));
	}

	public static previousPassWord: string;

	public static resetValidation(): void {
		PasswordValidator.validation = new PasswordValidationFields();
		PasswordValidator._isValidPassSub.next(false);
		PasswordValidator._isValidRepeatPassSub.next(false);
	}

	public static checkSpaceChar(value: string) {
		let spacePattern = new RegExp('[ ]');
		if (value.match(spacePattern)) this.validation.spaceCharExists = true;
		else this.validation.spaceCharExists = false;
	}

	public static checkAllowedCharacters(value: string) {
		let allowedCharactersPattern = new RegExp(
			/^[abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890\`\~\!\@\#\$\%\^\&\*\(\)\-_\=\+\[\{\]\}\\\|\;\:\'\""\<\,\>\.\/\?€£]+$/
		);

		if (!value.match(allowedCharactersPattern)) {
			this.validation.allowedCharactersOk = false;
			return false;
		} else {
			this.validation.allowedCharactersOk = true;
			return true;
		}
	}

	public static checkDigits(value: string) {
		let digitsPattern = new RegExp('[0-9]');
		if (value.match(digitsPattern)) {
			this.validation.digitsOk = true;
			return true;
		} else {
			this.validation.digitsOk = false;
			return false;
		}
	}

	public static checkLowercase(value: string) {
		let lowercasePattern = new RegExp('[a-z]');
		if (value.match(lowercasePattern)) {
			this.validation.lowercaseOk = true;
			return true;
		} else {
			this.validation.lowercaseOk = false;
			return false;
		}
	}

	public static checkUppercase(value: string) {
		let uppercasePattern = new RegExp('[A-Z]');
		if (value.match(uppercasePattern)) {
			this.validation.uppercaseOk = true;
			return true;
		} else {
			this.validation.uppercaseOk = false;

			return false;
		}
	}

	public static checkSpecialCharacter(value: string) {
		let allowedSpecialCharPattern = new RegExp(
			/[\`\~\!\@\#\$\%\^\&\*\(\)\-_\=\+\[\{\]\}\\\|\;\:\'\""\<\,\>\.\/\?€£]+/
		);

		if (value.match(allowedSpecialCharPattern)) {
			this.validation.specialCharOk = true;
			return true;
		} else {
			this.validation.specialCharOk = false;
			return false;
		}
	}

	public static checkPasswordLength(value: string) {
		if (value && value.length >= 8 && value.length <= 15) {
			this.validation.lengthOk = true;
			return true;
		} else {
			this.validation.lengthOk = false;
			return false;
		}
	}

	public static CheckPasswordConfirmation(password1: string, newPassword: string) {
		if (password1 === newPassword) {
			this.validation.passwordConfirmOK = true;
			this.validation.passwordConfirmMessage = null;
			this._isValidRepeatPassSub.next(true);
			return this.validation;
		}
		if (password1 != newPassword) {
			this.validation.passwordConfirmOK = false;
			this.validation.passwordConfirmMessage = 'Δεν ταυτίζονται τα δύο Passwords';
			this._isValidRepeatPassSub.next(false);
			return this.validation;
		}
	}
	public static CheckPreviousAndNewPassword(newPassword: string) {
		if (newPassword === this.previousPassWord) {
			this.validation.passwordConfirmOK = false;
			this.validation.passwordConfirmMessage =
				'Το νέο Password δεν πρέπει να ταυτίζεται με τον προηγούμενο';
			this._isValidPassSub.next(false);
		} else {
			this.validation.passwordConfirmOK = true;
			this.validation.passwordConfirmMessage = null;
			this._isValidPassSub.next(true);
		}
		return this.validation;
	}

	public static checkFields(value: string) {
		let counter: number = 0;
		this.checkSpaceChar(value);
		this.checkPasswordLength(value);
		this.checkAllowedCharacters(value);
		if (this.checkDigits(value)) counter = counter + 1;
		if (this.checkLowercase(value)) counter = counter + 1;
		if (this.checkSpecialCharacter(value)) counter = counter + 1;
		if (this.checkUppercase(value)) counter = counter + 1;

		if (
			counter < 3 ||
			!this.validation.lengthOk ||
			this.validation.spaceCharExists ||
			!this.validation.allowedCharactersOk
		) {
			this._isValidPassSub.next(false);
		} else {
			this._isValidPassSub.next(true);
		}
		return this.validation;
	}
}
